import { useEffect, useId, useMemo, useState } from "react";
import { LuCalendarPlus } from "react-icons/lu";
import { Input, Modal } from "@iq/models";
import { createFollowUp } from "../../services/contact_service";
import "./Modal.css";

const FOLLOW_UP_METHODS = [
    { value: "phone", label: "Phone" },
    { value: "email", label: "Email" },
    { value: "meeting", label: "Meeting" },
];

function getTodayDateString() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function CreateFollowUpModal({ isOpen, contactId, onClose, onCreated }) {
    const formId = useId();
    const [form, setForm] = useState({
        method: "phone",
        followUpDate: getTodayDateString(),
        followUpTime: "09:00",
        nextStep: "",
        notes: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    // Ensure contactId is a valid number
    const validContactId = contactId ? Number(contactId) : null;

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setForm({
            method: "phone",
            followUpDate: getTodayDateString(),
            followUpTime: "09:00",
            nextStep: "",
            notes: "",
        });
        setSaving(false);
        setError("");
    }, [isOpen]);

    const selectedMethodLabel = useMemo(() => {
        const selected = FOLLOW_UP_METHODS.find((item) => item.value === form.method);
        return selected?.label || "";
    }, [form.method]);

    const canSave = useMemo(() => {
        return Boolean(validContactId)
            && Boolean(form.method)
            && Boolean(form.followUpDate)
            && Boolean(form.followUpTime)
            && Boolean(form.nextStep.trim())
            && !saving;
    }, [validContactId, form.followUpDate, form.followUpTime, form.method, form.nextStep, saving]);

    const actionButtons = useMemo(() => ([
        {
            key: "create",
            text: saving ? "Saving..." : "Create Follow Up",
            icon: <LuCalendarPlus />,
            type: "submit",
            form: formId,
            disabled: !canSave,
            color: "accent",
        },
    ]), [canSave, formId, saving]);

    function updateField(field, value) {
        setForm((prev) => ({ ...prev, [field]: value }));
    }

    async function handleSubmit(event) {
        event.preventDefault();
        if (!canSave) {
            return;
        }

        setError("");
        setSaving(true);
        try {
            const created = await createFollowUp({
                contactId: validContactId,
                method: form.method,
                followUpDate: form.followUpDate,
                followUpTime: form.followUpTime,
                nextStep: form.nextStep.trim(),
                notes: form.notes.trim(),
            });
            onCreated?.(created);
            onClose?.();
        } catch (err) {
            setError(err?.message || "Failed to create follow up");
        } finally {
            setSaving(false);
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Create Follow Up"
            icon={<LuCalendarPlus />}
            actionButtons={actionButtons}
            errorMessage={error}
            shrink={true}
        >
            <form id={formId} className="modal-form-container" onSubmit={handleSubmit}>
                <div className="form-fields-container">
                    <div className="modal-form-field">
                        <label htmlFor="followup-method">Method <span style={{ color: "var(--color-danger)" }}>*</span></label>
                        <Input
                            type="dropdown"
                            id="followup-method"
                            name="followup-method"
                            placeholder="Select method"
                            value={selectedMethodLabel}
                            allowCustom={false}
                            colorScheme="light"
                            ariaLabel="Follow-up method options"
                        >
                            {({ closeMenu }) => (
                                <>
                                    {FOLLOW_UP_METHODS.map((item) => (
                                        <button
                                            key={item.value}
                                            type="button"
                                            className="dropdown-menu-item"
                                            onClick={() => {
                                                updateField("method", item.value);
                                                closeMenu();
                                            }}
                                        >
                                            {item.label}
                                        </button>
                                    ))}
                                </>
                            )}
                        </Input>
                    </div>

                    <div className="modal-form-field">
                        <label htmlFor="followup-date">Follow-up Date <span style={{ color: "var(--color-danger)" }}>*</span></label>
                        <Input
                            id="followup-date"
                            type="date"
                            value={form.followUpDate}
                            onChange={(event) => updateField("followUpDate", event.target.value)}
                            colorScheme="light"
                        />
                    </div>

                    <div className="modal-form-field">
                        <label htmlFor="followup-time">Follow-up Time <span style={{ color: "var(--color-danger)" }}>*</span></label>
                        <Input
                            id="followup-time"
                            type="time"
                            value={form.followUpTime}
                            onChange={(event) => updateField("followUpTime", event.target.value)}
                            colorScheme="light"
                        />
                    </div>

                    <div className="modal-form-field wide-form-field">
                        <label htmlFor="followup-next-step">Next Step <span style={{ color: "var(--color-danger)" }}>*</span></label>
                        <Input
                            id="followup-next-step"
                            type="text"
                            placeholder="What should happen next?"
                            value={form.nextStep}
                            onChange={(event) => updateField("nextStep", event.target.value)}
                            colorScheme="light"
                        />
                    </div>

                    <div className="modal-form-field wide-form-field">
                        <label htmlFor="followup-notes">Notes <span className="modal-form-field-note">(optional)</span></label>
                        <Input
                            id="followup-notes"
                            type="textarea"
                            rows={4}
                            placeholder="Optional notes"
                            value={form.notes}
                            onChange={(event) => updateField("notes", event.target.value)}
                            colorScheme="light"
                        />
                    </div>
                </div>
            </form>
        </Modal>
    );
}

export default CreateFollowUpModal;
